import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SeoHead from '../../components/SeoHead';

const SITE_URL = 'https://gvnemt.com';
const SHARE_TEXT = encodeURIComponent(
  'I just connected with Guardian Veterans LLC — a veteran-owned medical transportation service launching in the West Valley AZ area. Check them out!'
);
const SHARE_URL = encodeURIComponent(SITE_URL);

type FormType = 'waitlist' | 'private-pay' | 'facility' | 'contact' | 'notify' | 'general';

interface FormConfig {
  icon: string;
  iconBg: string;
  badge: string;
  heading: string;
  subheading: string;
  steps: { icon: string; title: string; description: string }[];
  cta: { label: string; target: string };
}

const formConfigs: Record<FormType, FormConfig> = {
  waitlist: {
    icon: 'ri-list-check-2',
    iconBg: 'bg-yellow-500',
    badge: 'Early Access Waitlist',
    heading: "You're on the Waitlist!",
    subheading:
      "You're among the first in line. Here's what happens next as we prepare to launch in the West Valley.",
    steps: [
      {
        icon: 'ri-mail-check-line',
        title: 'Watch Your Inbox',
        description:
          "We'll send you an email before we officially launch — you'll be among the very first to know when scheduling opens in your area.",
      },
      {
        icon: 'ri-phone-line',
        title: 'We May Reach Out Directly',
        description:
          'A team member may call or text you to confirm your transportation needs and make sure we have everything ready for your first ride.',
      },
      {
        icon: 'ri-calendar-check-line',
        title: 'Priority Scheduling at Launch',
        description:
          'As a waitlist member, you get priority access to book your first ride before we open to the general public.',
      },
      {
        icon: 'ri-share-line',
        title: 'Spread the Word',
        description:
          'Know a neighbor, family member, or friend who needs reliable medical transportation? Share our site — the more we know about demand, the faster we can launch.',
      },
    ],
    cta: { label: 'Back to Home', target: '/' },
  },
  'private-pay': {
    icon: 'ri-user-heart-line',
    iconBg: 'bg-yellow-500',
    badge: 'Private Pay Interest',
    heading: 'Your Interest Has Been Received!',
    subheading:
      'Thank you for expressing interest in private pay transportation — whether for ambulatory, wheelchair, or gurney / stretcher transport. Here\'s what to expect from our team.',
    steps: [
      {
        icon: 'ri-file-search-line',
        title: "We'll Review Your Needs",
        description:
          'Our team will carefully review the transportation details you submitted — including transport type (ambulatory, wheelchair, or gurney / stretcher), schedule, frequency, and any special crew or equipment requirements.',
      },
      {
        icon: 'ri-phone-line',
        title: 'Expect a Call or Email Within 1–2 Business Days',
        description:
          'A Guardian Veterans team member will reach out to confirm your transport type, discuss your needs, answer questions, and verify availability in your area.',
      },
      {
        icon: 'ri-calendar-2-line',
        title: 'First Ride Scheduling',
        description:
          "Once we launch, you'll be among the first contacted to schedule your first ride. For gurney / stretcher requests, we'll confirm two-person crew availability and ensure everything is in place for a safe, comfortable transport.",
      },
      {
        icon: 'ri-heart-line',
        title: 'Compassionate, Veteran-Owned Service',
        description:
          'Every ride — ambulatory, wheelchair, or stretcher — is handled with care, dignity, and professionalism. We treat every passenger the way we would want our own family treated.',
      },
    ],
    cta: { label: 'Back to Home', target: '/' },
  },
  facility: {
    icon: 'ri-building-2-line',
    iconBg: 'bg-slate-700',
    badge: 'Facility Transportation Survey',
    heading: 'Survey Received — Thank You!',
    subheading:
      "Your facility's transportation survey is in our hands. Here's what our partnership process looks like.",
    steps: [
      {
        icon: 'ri-file-list-3-line',
        title: "We'll Review Your Facility's Needs",
        description:
          "Our operations team will review your survey responses to understand your resident volume, transportation frequency, and any special requirements your facility has.",
      },
      {
        icon: 'ri-calendar-schedule-line',
        title: 'Introduction Meeting Within 1 Business Day',
        description:
          "We'll reach out within 1 business day to schedule a brief introduction call or meeting to discuss how Guardian Veterans can best serve your residents.",
      },
      {
        icon: 'ri-star-line',
        title: 'Founding Partner Benefits',
        description:
          'Facilities that partner with us before launch receive priority scheduling, preferred pricing, and dedicated account support — locked in at our founding partner rates.',
      },
      {
        icon: 'ri-handshake-line',
        title: 'Partnership Agreement',
        description:
          "Once we align on your needs, we'll formalize the partnership so your residents have guaranteed access to reliable NEMT services from day one.",
      },
    ],
    cta: { label: 'Back to Home', target: '/' },
  },
  contact: {
    icon: 'ri-mail-send-line',
    iconBg: 'bg-yellow-500',
    badge: 'Contact Message',
    heading: 'Message Sent Successfully!',
    subheading:
      "We've received your message and will get back to you as soon as possible. Here's what to expect.",
    steps: [
      {
        icon: 'ri-time-line',
        title: 'Reply Within 24 Hours',
        description:
          "Our team monitors messages closely. You can expect a reply within 24 hours on business days. If it's urgent, please call us directly.",
      },
      {
        icon: 'ri-phone-line',
        title: 'Need Faster Help? Call Us',
        description:
          'For time-sensitive questions, call us directly at (602) 295-6626. We\'re available Monday–Friday, 6:30 AM – 6 PM MST.',
      },
      {
        icon: 'ri-mail-line',
        title: 'Check Your Spam Folder',
        description:
          'Our reply will come from info@gvnemt.com — if you don\'t see it in your inbox within 24 hours, please check your spam or junk folder.',
      },
    ],
    cta: { label: 'Back to Home', target: '/' },
  },
  notify: {
    icon: 'ri-notification-3-line',
    iconBg: 'bg-yellow-500',
    badge: 'Launch Notification',
    heading: "You'll Be Notified at Launch!",
    subheading:
      "We've added you to our launch notification list. Here's what happens next.",
    steps: [
      {
        icon: 'ri-mail-check-line',
        title: 'Launch Email Coming Your Way',
        description:
          "When Guardian Veterans officially launches in the West Valley, you'll receive an email notification so you can book or refer someone right away.",
      },
      {
        icon: 'ri-user-add-line',
        title: 'Want Full Priority Access?',
        description:
          "Consider filling out our Early Access Waitlist form to get priority scheduling and be among the very first riders when we launch.",
      },
      {
        icon: 'ri-share-line',
        title: 'Share With Someone Who Needs It',
        description:
          'Know a veteran, senior, dialysis patient, or someone needing stretcher / gurney transport in the West Valley? Share our site — your referral could make a real difference.',
      },
    ],
    cta: { label: 'Join the Full Waitlist', target: '/#waitlist' },
  },
  general: {
    icon: 'ri-shield-star-line',
    iconBg: 'bg-yellow-500',
    badge: 'Form Submitted',
    heading: 'Thank You for Reaching Out!',
    subheading:
      "We've received your submission. A member of the Guardian Veterans team will follow up with you shortly.",
    steps: [
      {
        icon: 'ri-time-line',
        title: 'Expect a Follow-Up Soon',
        description:
          'Our team will review your submission and reach out within 1–2 business days to follow up.',
      },
      {
        icon: 'ri-phone-line',
        title: 'Questions? Call Us',
        description:
          'Reach us directly at (602) 295-6626, Monday–Friday, 6:30 AM – 6 PM MST.',
      },
      {
        icon: 'ri-share-line',
        title: 'Spread the Word',
        description:
          'Help us reach more people who need reliable medical transportation in the West Valley by sharing our site.',
      },
    ],
    cta: { label: 'Back to Home', target: '/' },
  },
};

export default function ThankYouPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  const formParam = searchParams.get('form') as FormType | null;
  const formType: FormType =
    formParam && formConfigs[formParam] ? formParam : 'general';
  const config = formConfigs[formType];

  useEffect(() => {
    const timer = setTimeout(() => setAnimateIn(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(SITE_URL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleCta = () => {
    const target = config.cta.target;
    if (target.startsWith('/#')) {
      navigate('/');
      setTimeout(() => {
        const id = target.replace('/#', '');
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      navigate(target);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <SeoHead
        title="Thank You | Guardian Veterans LLC"
        description="Thank you for connecting with Guardian Veterans LLC. Here are your next steps."
        canonical={`${import.meta.env.VITE_SITE_URL}/thank-you`}
      />

      {/* Nav */}
      <nav className="bg-slate-900 border-b border-yellow-500/20 px-8 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-3 cursor-pointer"
        >
          <div className="flex flex-col leading-none">
            <span className="text-white font-extrabold text-xl tracking-widest uppercase">
              Guardian Veterans
            </span>
            <span className="text-yellow-400 font-bold text-xs tracking-[0.3em] uppercase">
              LLC
            </span>
          </div>
        </button>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-300 hover:text-yellow-400 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          Back to Home
        </button>
      </nav>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-start py-16 px-6">
        <div
          className={`w-full max-w-3xl transition-all duration-700 ${
            animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {/* Hero Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden mb-8">
            {/* Top Banner */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-8 py-10 text-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-10"
                style={{
                  backgroundImage:
                    'repeating-linear-gradient(45deg, #f59e0b 0, #f59e0b 1px, transparent 0, transparent 50%)',
                  backgroundSize: '12px 12px',
                }}
              ></div>
              <div className="relative z-10">
                <div
                  className={`w-20 h-20 ${config.iconBg} rounded-full flex items-center justify-center mx-auto mb-5 shadow-lg`}
                >
                  <i className={`${config.icon} text-white text-4xl`}></i>
                </div>
                <span className="inline-block bg-yellow-500/20 text-yellow-400 text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4">
                  {config.badge}
                </span>
                <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-3">
                  {config.heading}
                </h1>
                <p className="text-gray-300 text-base max-w-xl mx-auto leading-relaxed">
                  {config.subheading}
                </p>
              </div>
            </div>

            {/* Steps */}
            <div className="px-8 py-10">
              <h2 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                <i className="ri-route-line text-yellow-500 text-xl"></i>
                Your Next Steps
              </h2>
              <div className="space-y-5">
                {config.steps.map((step, idx) => (
                  <div
                    key={idx}
                    className={`flex gap-5 p-5 rounded-xl border border-slate-100 bg-slate-50 transition-all duration-500`}
                    style={{ transitionDelay: `${idx * 100 + 200}ms` }}
                  >
                    <div className="flex-shrink-0 flex flex-col items-center gap-1">
                      <div className="w-10 h-10 bg-yellow-500 rounded-xl flex items-center justify-center shadow-sm">
                        <i className={`${step.icon} text-black text-lg`}></i>
                      </div>
                      {idx < config.steps.length - 1 && (
                        <div className="w-0.5 flex-1 bg-yellow-200 min-h-[20px]"></div>
                      )}
                    </div>
                    <div className="pt-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-yellow-600 bg-yellow-50 border border-yellow-200 rounded-full px-2 py-0.5">
                          Step {idx + 1}
                        </span>
                      </div>
                      <h3 className="text-base font-bold text-slate-800 mb-1">
                        {step.title}
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleCta}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-400 text-black font-bold py-3.5 rounded-xl transition-all cursor-pointer whitespace-nowrap text-sm tracking-wide flex items-center justify-center gap-2 shadow-sm hover:shadow-md hover:-translate-y-0.5"
                >
                  <i className="ri-arrow-right-line"></i>
                  {config.cta.label}
                </button>
                <button
                  onClick={() => navigate('/services')}
                  className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold py-3.5 rounded-xl transition-all cursor-pointer whitespace-nowrap text-sm tracking-wide flex items-center justify-center gap-2 shadow-sm hover:shadow-md hover:-translate-y-0.5"
                >
                  <i className="ri-service-line"></i>
                  View Our Services
                </button>
              </div>
            </div>
          </div>

          {/* Share Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 px-8 py-8">
            <div className="text-center mb-6">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                <i className="ri-share-forward-line text-yellow-500 text-2xl"></i>
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-1">
                Help Us Reach More People
              </h2>
              <p className="text-slate-500 text-sm max-w-md mx-auto">
                Know a veteran, senior, dialysis patient, or someone needing stretcher / gurney transport in the West Valley? Share Guardian Veterans with them.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-3">
              {/* Facebook */}
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${SHARE_URL}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center gap-2 bg-[#1877F2] hover:bg-[#1565d8] text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all cursor-pointer whitespace-nowrap shadow-sm hover:shadow-md hover:-translate-y-0.5"
              >
                <i className="ri-facebook-fill text-base"></i>
                Share on Facebook
              </a>

              {/* X / Twitter */}
              <a
                href={`https://twitter.com/intent/tweet?text=${SHARE_TEXT}&url=${SHARE_URL}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center gap-2 bg-black hover:bg-slate-800 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all cursor-pointer whitespace-nowrap shadow-sm hover:shadow-md hover:-translate-y-0.5"
              >
                <i className="ri-twitter-x-line text-base"></i>
                Share on X
              </a>

              {/* Nextdoor */}
              <a
                href={`https://nextdoor.com/share/?body=${SHARE_TEXT}&url=${SHARE_URL}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center gap-2 bg-[#00B246] hover:bg-[#009a3c] text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all cursor-pointer whitespace-nowrap shadow-sm hover:shadow-md hover:-translate-y-0.5"
              >
                <i className="ri-community-line text-base"></i>
                Nextdoor
              </a>

              {/* Copy Link */}
              <button
                onClick={handleCopyLink}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all cursor-pointer whitespace-nowrap shadow-sm hover:shadow-md hover:-translate-y-0.5 ${
                  copied
                    ? 'bg-green-500 text-white'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                }`}
              >
                <i className={copied ? 'ri-check-line text-base' : 'ri-links-line text-base'}></i>
                {copied ? 'Link Copied!' : 'Copy Link'}
              </button>
            </div>
          </div>

          {/* Contact Strip */}
          <div className="mt-6 bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 border border-yellow-500/20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-yellow-500 rounded-xl">
                <i className="ri-shield-star-line text-black text-lg"></i>
              </div>
              <div>
                <p className="text-white font-bold text-sm">Questions? We're here.</p>
                <p className="text-slate-400 text-xs">Veteran-Owned &amp; Operated — West Valley, AZ</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <a
                href="tel:+16022956626"
                className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                (602) 295-6626
              </a>
              <a
                href="mailto:info@gvnemt.com"
                className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-line"></i>
                Email Us
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Footer Strip */}
      <footer className="bg-slate-900 border-t border-yellow-500/20 py-5 px-8 text-center">
        <p className="text-slate-500 text-xs">
          © 2026 Guardian Veterans LLC — All Rights Reserved &nbsp;·&nbsp;
          <span className="text-yellow-500 italic">"We stand for those who stood for us."</span>
        </p>
      </footer>
    </div>
  );
}
